/* Created by Interactive 63 - https://interactive63.com */
/**
 * The keyframe area. A single canvas draws rows, keyframes and connecting lines; hit testing is
 * done in code. The playhead is a separate DOM element so playback never forces a canvas redraw.
 * A visually hidden list of buttons mirrors the keyframes for keyboard and screen reader users.
 */
import { PROPERTIES } from '../../../core/properties/registry';
import { findKeyframe } from '../../../core/timeline/keyframes';
import type { PropertyValue } from '../../../core/model/types';
import { isDrawValue, isVec2 } from '../../../core/model/types';
import { canDistribute } from '../../../core/timeline/distribute';
import { copySelection, duplicateAtPlayhead, pasteMemory } from '../../clipboard';
import { setKeyframeEasing } from '../../store/actions';
import { setKeyframeTime } from '../../store/property-actions';
import {
  addKeyframeAtTime,
  deleteKeyframes,
  distributeKeyframes,
  dragKeyframesFromBase,
  duplicateKeyframesBy,
  reverseTiming,
  selectAllKeyframesOn,
  selectKeyframesWithElements,
} from '../../store/timeline-actions';
import { formatTime } from '../../time-format';
import { APPEARANCE_EVENT } from '../../appearance';
import { h } from '../../ui/dom';
import { defaultEasingFor, EASING_CHOICES } from '../inspector/easing-editor';
import { KeyframeSection } from '../inspector/keyframe-section';
import { showContextMenu, type MenuItem } from './context-menu';
import type { TimelineHost } from './host';
import {
  computeDragDelta,
  hitTestMark,
  marqueeMarks,
  markPosition,
  normaliseRect,
  selectionAfterClick,
  type Rect,
} from './interactions';
import { rowAtY, totalHeight, type KeyMark } from './layout';
import { computeTicks, timeToX, xToTime } from './ruler';
import { ghostTimes, repeatPeriod } from '../../../core/timeline/repeat';

const MARK_SIZE = 5.5;
const DRAG_THRESHOLD_PX = 3;

interface DragState {
  mark: KeyMark;
  ids: string[];
  baseTracks: ReturnType<TimelineHost['store']['getState']>['project']['tracks'];
  offsetTime: number;
  startX: number;
  alt: boolean;
  moved: boolean;
  delta: number;
  clickedSelectedGroup: boolean;
  modifier: boolean;
}

interface MarqueeState {
  rect: Rect;
  additive: boolean;
  moved: boolean;
  startX: number;
  startY: number;
}

/** Reads a CSS custom property from the document root. */
function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/** Formats a keyframe value for a tooltip. */
function describeValue(value: PropertyValue): string {
  if (typeof value === 'number') return String(Number(value.toFixed(3)));
  if (typeof value === 'string') return value;
  if (isVec2(value)) return `${Number(value.x.toFixed(2))}, ${Number(value.y.toFixed(2))}`;
  if (isDrawValue(value))
    return `${Number(value.start.toFixed(1))}% to ${Number(value.end.toFixed(1))}%`;
  return 'path';
}

/** Formats a time in seconds for the drag tooltip. */
function seconds(ms: number): string {
  return `${Number((ms / 1000).toFixed(3))}s`;
}

export class TrackCanvas {
  readonly el: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly playhead: HTMLElement;
  private readonly tooltip: HTMLElement;
  private readonly keyList: HTMLElement;
  private frame = 0;
  private a11yFrame = 0;
  private drag: DragState | null = null;
  private marquee: MarqueeState | null = null;
  private ghosts: { time: number; rowIndex: number }[] = [];
  private guide: number | null = null;
  private hovered: KeyMark | null = null;
  private scrollTop = 0;
  private viewportHeight = 0;
  private popoverCleanup: (() => void) | null = null;

  /** Builds the canvas area. */
  constructor(private readonly host: TimelineHost) {
    // The canvas reads its colours when it draws, so a change of look needs a redraw.
    window.addEventListener(APPEARANCE_EVENT, () => {
      this.requestDraw();
    });
    this.canvas = h('canvas', { class: 'track-canvas' });
    this.playhead = h('div', { class: 'playhead', 'aria-hidden': 'true' });
    this.tooltip = h('div', { class: 'timeline-tooltip', hidden: true, role: 'tooltip' });
    this.keyList = h('div', { class: 'visually-hidden', role: 'group', 'aria-label': 'Keyframes' });
    this.el = h(
      'div',
      { class: 'track-area' },
      this.canvas,
      this.playhead,
      this.tooltip,
      this.keyList,
    );
    this.bindPointer();
    this.canvas.addEventListener('contextmenu', (event) => {
      this.onContextMenu(event);
    });
    this.canvas.addEventListener('dblclick', (event) => {
      this.onDoubleClick(event);
    });
  }

  /** Records the visible vertical window so off-screen rows are not drawn. */
  setScrollWindow(top: number, height: number): void {
    this.scrollTop = top;
    this.viewportHeight = height;
    this.requestDraw();
  }

  /** Moves the playhead line. */
  setPlayhead(ms: number): void {
    const x = timeToX(ms, this.host.getView());
    this.playhead.style.transform = `translateX(${x}px)`;
    this.playhead.hidden = x < -2 || x > this.host.getWidth() + 2;
  }

  /** Called when rows or marks changed: redraws and rebuilds the accessible list. */
  modelChanged(): void {
    this.requestDraw();
    if (this.a11yFrame) return;
    this.a11yFrame = requestAnimationFrame(() => {
      this.a11yFrame = 0;
      this.rebuildKeyList();
    });
  }

  /** Schedules a redraw on the next animation frame. */
  requestDraw(): void {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.draw();
    });
  }

  /** Converts a pointer event to canvas coordinates. */
  private local(event: MouseEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  /** The IDs of every selected keyframe. */
  private selectedIds(): string[] {
    return this.host.store.getState().editor.selection.keyframeIds;
  }

  /** Draws rows, grid, lines, keyframes, ghosts, the marquee and the snap guide. */
  draw(): void {
    const width = this.host.getWidth();
    const rows = this.host.getRows();
    const height = Math.max(totalHeight(rows), this.viewportHeight);
    const dpr = window.devicePixelRatio || 1;
    if (
      this.canvas.width !== Math.round(width * dpr) ||
      this.canvas.height !== Math.round(height * dpr)
    ) {
      this.canvas.width = Math.round(width * dpr);
      this.canvas.height = Math.round(height * dpr);
      this.canvas.style.width = `${width}px`;
      this.canvas.style.height = `${height}px`;
    }
    const g = this.canvas.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, width, height);

    const view = this.host.getView();
    const colours = {
      border: cssVar('--border'),
      raised: cssVar('--panel-raised'),
      key: cssVar('--keyframe'),
      selected: cssVar('--keyframe-selected'),
      accent: cssVar('--accent'),
      soft: cssVar('--accent-soft'),
      faint: cssVar('--text-faint'),
    };
    const top = this.scrollTop;
    const bottom = this.scrollTop + (this.viewportHeight || height);

    // Row backgrounds and separators.
    for (const row of rows) {
      if (row.y + row.height < top || row.y > bottom) continue;
      if (row.kind === 'element') {
        g.globalAlpha = 0.55;
        g.fillStyle = colours.raised;
        g.fillRect(0, row.y, width, row.height);
      }
      g.globalAlpha = 0.5;
      g.fillStyle = colours.border;
      g.fillRect(0, row.y + row.height - 1, width, 1);
    }

    // Vertical grid at major ticks.
    g.globalAlpha = 0.35;
    g.fillStyle = colours.border;
    for (const tick of computeTicks(view, width).major)
      g.fillRect(Math.round(tick.x), top, 1, bottom - top);

    // Everything after the scene end is shaded.
    const duration =
      this.host.getPendingDuration() ?? this.host.store.getState().project.scene.duration;
    const endX = timeToX(duration, view);
    g.globalAlpha = 0.28;
    g.fillStyle = '#000';
    if (endX < width) g.fillRect(Math.max(0, endX), top, width - Math.max(0, endX), bottom - top);
    g.globalAlpha = 1;
    g.fillStyle = colours.accent;
    g.fillRect(Math.round(endX), top, 1, bottom - top);

    // Lines between consecutive keyframes on each track row.
    for (const row of rows) {
      if (row.kind !== 'track' || !row.track || row.y + row.height < top || row.y > bottom)
        continue;
      const keyframes = row.track.keyframes;
      g.globalAlpha = row.track.enabled ? 0.65 : 0.25;
      g.strokeStyle = colours.faint;
      g.lineWidth = 1.5;
      const y = Math.round(row.y + row.height / 2) + 0.5;
      for (let i = 0; i < keyframes.length - 1; i += 1) {
        const from = keyframes[i];
        const to = keyframes[i + 1];
        if (!from || !to) continue;
        g.setLineDash(from.easing.type === 'hold' ? [4, 3] : []);
        g.beginPath();
        g.moveTo(timeToX(from.time, view), y);
        g.lineTo(timeToX(to.time, view), y);
        g.stroke();
      }
    }
    g.setLineDash([]);

    // Repeating tracks: a bracket over the first cycle and faint copies of its keyframes after it.
    for (const row of rows) {
      if (row.kind !== 'track' || !row.track?.repeat || row.y + row.height < top || row.y > bottom)
        continue;
      const period = repeatPeriod(row.track);
      if (period === undefined) continue;
      const y = row.y + 3.5;
      g.globalAlpha = 0.7;
      g.strokeStyle = colours.accent;
      g.lineWidth = 1;
      g.beginPath();
      g.moveTo(timeToX(0, view), y + 3);
      g.lineTo(timeToX(0, view), y);
      g.lineTo(timeToX(period, view), y);
      g.lineTo(timeToX(period, view), y + 3);
      g.stroke();
      g.globalAlpha = row.track.enabled ? 0.35 : 0.15;
      const midY = row.y + row.height / 2;
      for (const time of ghostTimes(row.track, duration)) {
        const x = timeToX(time, view);
        if (x < -10 || x > width + 10) continue;
        this.drawShape(g, 'diamond', x, midY, colours.key, null);
      }
    }
    g.globalAlpha = 1;

    // Keyframes.
    const selected = new Set(this.selectedIds());
    for (const mark of this.host.getMarks()) {
      const { x, y } = markPosition(mark, rows, view);
      if (x < -10 || x > width + 10 || y < top - 10 || y > bottom + 10) continue;
      const isSelected = mark.ids.every((id) => selected.has(id));
      g.globalAlpha = mark.trackEnabled ? (mark.merged ? 0.85 : 1) : 0.4;
      this.drawShape(
        g,
        mark.shape,
        x,
        y,
        isSelected ? colours.selected : colours.key,
        isSelected ? colours.accent : null,
      );
    }

    // Ghost copies while Alt-dragging.
    g.globalAlpha = 0.55;
    for (const ghost of this.ghosts) {
      const row = rows[ghost.rowIndex];
      if (row)
        this.drawShape(
          g,
          'diamond',
          timeToX(ghost.time, view),
          row.y + row.height / 2,
          colours.key,
          colours.accent,
        );
    }

    // Snap guide.
    if (this.guide !== null) {
      g.globalAlpha = 0.9;
      g.strokeStyle = colours.accent;
      g.setLineDash([3, 3]);
      const x = Math.round(timeToX(this.guide, view)) + 0.5;
      g.beginPath();
      g.moveTo(x, top);
      g.lineTo(x, bottom);
      g.stroke();
      g.setLineDash([]);
    }

    // Marquee.
    if (this.marquee?.moved) {
      const box = normaliseRect(this.marquee.rect);
      g.globalAlpha = 0.18;
      g.fillStyle = colours.soft;
      g.fillRect(box.x1, box.y1, box.x2 - box.x1, box.y2 - box.y1);
      g.globalAlpha = 1;
      g.strokeStyle = colours.accent;
      g.lineWidth = 1;
      g.strokeRect(box.x1 + 0.5, box.y1 + 0.5, box.x2 - box.x1, box.y2 - box.y1);
    }
    g.globalAlpha = 1;
  }

  /** Draws one keyframe shape, with an optional selection ring. */
  private drawShape(
    g: CanvasRenderingContext2D,
    shape: KeyMark['shape'],
    x: number,
    y: number,
    fill: string,
    ring: string | null,
  ): void {
    g.beginPath();
    if (shape === 'circle') g.arc(x, y, MARK_SIZE - 1, 0, Math.PI * 2);
    else if (shape === 'square')
      g.rect(
        x - MARK_SIZE + 1.5,
        y - MARK_SIZE + 1.5,
        (MARK_SIZE - 1.5) * 2,
        (MARK_SIZE - 1.5) * 2,
      );
    else {
      g.moveTo(x, y - MARK_SIZE);
      g.lineTo(x + MARK_SIZE, y);
      g.lineTo(x, y + MARK_SIZE);
      g.lineTo(x - MARK_SIZE, y);
      g.closePath();
    }
    g.fillStyle = fill;
    g.fill();
    g.lineWidth = ring ? 2 : 1;
    g.strokeStyle = ring ?? 'rgb(0 0 0 / 0.35)';
    g.stroke();
  }

  /** Shows the tooltip near a pointer position. */
  private showTooltip(text: string, event: MouseEvent): void {
    const area = this.el.getBoundingClientRect();
    this.tooltip.textContent = text;
    this.tooltip.hidden = false;
    this.tooltip.style.left = `${event.clientX - area.left + 12}px`;
    this.tooltip.style.top = `${event.clientY - area.top + 14}px`;
  }

  /** Describes a mark for the hover tooltip. */
  private describeMark(mark: KeyMark): string {
    if (mark.merged)
      return `${formatTime(mark.time)}: ${mark.ids.length} keyframe${mark.ids.length === 1 ? '' : 's'}`;
    const found = findKeyframe(this.host.store.getState().project, mark.id);
    const label = mark.property ? PROPERTIES[mark.property].label : '';
    return `${label} ${formatTime(mark.time)}${found ? `: ${describeValue(found.keyframe.value)}` : ''}`;
  }

  /** Wires pointer interaction: selection, dragging, marquee, hover and wheel. */
  private bindPointer(): void {
    this.canvas.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      const { x, y } = this.local(event);
      const view = this.host.getView();
      const mark = hitTestMark(this.host.getMarks(), this.host.getRows(), view, x, y);
      this.canvas.setPointerCapture(event.pointerId);
      this.tooltip.hidden = true;

      if (!mark) {
        this.marquee = {
          rect: { x1: x, y1: y, x2: x, y2: y },
          additive: event.shiftKey || event.ctrlKey || event.metaKey,
          moved: false,
          startX: x,
          startY: y,
        };
        return;
      }

      const current = this.selectedIds();
      const modifier = event.shiftKey || event.ctrlKey || event.metaKey;
      const alreadySelected = mark.ids.every((id) => current.includes(id));
      if (modifier) {
        selectKeyframesWithElements(
          this.host.store,
          selectionAfterClick(current, mark.ids, {
            shift: event.shiftKey,
            ctrl: event.ctrlKey || event.metaKey,
          }),
        );
      } else if (!alreadySelected) {
        selectKeyframesWithElements(this.host.store, mark.ids);
      }
      const ids = this.selectedIds();
      if (!ids.some((id) => mark.ids.includes(id))) return;
      this.drag = {
        mark,
        ids: [...ids],
        baseTracks: this.host.store.getState().project.tracks,
        offsetTime: xToTime(x, view) - mark.time,
        startX: x,
        alt: event.altKey,
        moved: false,
        delta: 0,
        clickedSelectedGroup: alreadySelected && !modifier && ids.length > mark.ids.length,
        modifier,
      };
    });

    this.canvas.addEventListener('pointermove', (event) => {
      const { x, y } = this.local(event);
      if (this.drag) {
        this.onDrag(event, x);
        return;
      }
      if (this.marquee) {
        this.marquee.rect = { x1: this.marquee.startX, y1: this.marquee.startY, x2: x, y2: y };
        if (Math.hypot(x - this.marquee.startX, y - this.marquee.startY) > DRAG_THRESHOLD_PX) {
          this.marquee.moved = true;
        }
        this.requestDraw();
        return;
      }
      const mark = hitTestMark(
        this.host.getMarks(),
        this.host.getRows(),
        this.host.getView(),
        x,
        y,
      );
      if (mark?.id !== this.hovered?.id || mark?.merged !== this.hovered?.merged)
        this.hovered = mark ?? null;
      if (mark) this.showTooltip(this.describeMark(mark), event);
      else this.tooltip.hidden = true;
      this.canvas.style.cursor = mark ? 'grab' : 'default';
    });

    this.canvas.addEventListener('pointerup', (event) => {
      const { x } = this.local(event);
      if (this.drag) this.endDrag(event);
      else if (this.marquee) this.endMarquee(x);
    });
    this.canvas.addEventListener('pointercancel', () => {
      this.drag = null;
      this.marquee = null;
      this.ghosts = [];
      this.guide = null;
      this.requestDraw();
    });
    this.canvas.addEventListener('pointerleave', () => {
      if (!this.drag) this.tooltip.hidden = true;
    });
  }

  /** Updates a drag: moves the keyframes live, or shows ghosts when duplicating with Alt. */
  private onDrag(event: PointerEvent, x: number): void {
    const drag = this.drag;
    if (!drag) return;
    if (!drag.moved && Math.abs(x - drag.startX) <= DRAG_THRESHOLD_PX) return;
    drag.moved = true;

    const { store, playback } = this.host;
    const view = this.host.getView();
    const { project, editor } = store.getState();
    const dragged = new Set(drag.ids);
    const times = new Map<string, number>();
    const targets: number[] = [playback.currentTime];
    for (const track of drag.baseTracks) {
      for (const kf of track.keyframes) {
        times.set(kf.id, kf.time);
        if (!dragged.has(kf.id)) targets.push(kf.time);
      }
    }
    const result = computeDragDelta({
      grabbedTime: drag.mark.time,
      pointerTime: xToTime(x, view) - drag.offsetTime,
      draggedTimes: drag.ids.map((id) => times.get(id) ?? 0),
      targets,
      duration: project.scene.duration,
      zoom: view.zoom,
      snapping: editor.snapping && !event.shiftKey,
    });
    drag.delta = result.delta;
    this.guide = result.guide;

    if (drag.alt) {
      this.ghosts = this.host
        .getMarks()
        .filter((m) => m.ids.some((id) => dragged.has(id)) && !m.merged)
        .map((m) => ({ time: m.time + result.delta, rowIndex: m.rowIndex }));
    } else {
      dragKeyframesFromBase(store, drag.baseTracks, drag.ids, result.delta);
    }
    const landed = drag.mark.time + result.delta;
    const sign = result.delta >= 0 ? '+' : '−';
    this.showTooltip(`${seconds(landed)} (${sign}${Math.abs(result.delta)}ms)`, event);
    this.requestDraw();
  }

  /** Finishes a drag: commits an Alt-drag duplicate, or on a plain click narrows the selection and seeks to the keyframe. */
  private endDrag(event: PointerEvent): void {
    const drag = this.drag;
    this.drag = null;
    this.guide = null;
    this.ghosts = [];
    this.tooltip.hidden = true;
    if (!drag) return;
    if (drag.moved && drag.alt && drag.delta !== 0) {
      duplicateKeyframesBy(this.host.store, drag.ids, drag.delta);
    } else if (!drag.moved && drag.clickedSelectedGroup && !event.shiftKey) {
      selectKeyframesWithElements(this.host.store, drag.mark.ids);
    }
    // Inspector edits write at the playhead, so a plain click brings the playhead to the keyframe.
    if (!drag.moved && !drag.modifier && !drag.alt) {
      if (this.host.playback.isPlaying) this.host.playback.pause();
      this.host.playback.seek(drag.mark.time);
    }
    this.requestDraw();
  }

  /** Finishes a marquee: selects what it covers, or treats it as a click on empty space. */
  private endMarquee(x: number): void {
    const marquee = this.marquee;
    this.marquee = null;
    if (!marquee) return;
    const { store, playback } = this.host;
    if (marquee.moved) {
      const hit = marqueeMarks(
        this.host.getMarks(),
        this.host.getRows(),
        this.host.getView(),
        marquee.rect,
      );
      const ids = hit.flatMap((m) => m.ids);
      const next = marquee.additive ? [...new Set([...this.selectedIds(), ...ids])] : ids;
      selectKeyframesWithElements(store, next);
    } else {
      if (!marquee.additive) {
        store.updateEditor((draft) => {
          draft.selection.keyframeIds = [];
        });
      }
      const duration = store.getState().project.scene.duration;
      const time = Math.min(duration, Math.max(0, Math.round(xToTime(x, this.host.getView()))));
      if (playback.isPlaying) playback.pause();
      playback.seek(time);
    }
    this.requestDraw();
  }

  /** Double-click: edit a keyframe's easing, or add a keyframe on an animated track row. */
  private onDoubleClick(event: MouseEvent): void {
    const { x, y } = this.local(event);
    const view = this.host.getView();
    const mark = hitTestMark(this.host.getMarks(), this.host.getRows(), view, x, y);
    if (mark) {
      selectKeyframesWithElements(this.host.store, mark.ids);
      this.openKeyframePopover(mark.ids, event.clientX, event.clientY);
      return;
    }
    const row = this.host.getRows()[rowAtY(this.host.getRows(), y)];
    if (row?.kind !== 'track' || !row.track) return;
    const duration = this.host.store.getState().project.scene.duration;
    const time = Math.min(duration, Math.max(0, Math.round(xToTime(x, view) / 10) * 10));
    const id = addKeyframeAtTime(this.host.store, row.elementId, row.track.property, time);
    if (id) selectKeyframesWithElements(this.host.store, [id]);
  }

  /** Opens the keyframe section as a popover near a screen position. */
  private openKeyframePopover(ids: readonly string[], clientX: number, clientY: number): void {
    this.popoverCleanup?.();
    const section = new KeyframeSection(this.host.inspector, ids);
    const popover = h(
      'div',
      {
        class: 'popover timeline-popover',
        popover: 'auto',
        role: 'dialog',
        'aria-label': 'Keyframe easing',
      },
      section.el,
    );
    this.el.append(popover);
    if (typeof popover.showPopover !== 'function') return;
    popover.showPopover();
    popover.style.left = `${Math.max(8, Math.min(window.innerWidth - popover.offsetWidth - 8, clientX))}px`;
    popover.style.top = `${Math.max(8, Math.min(window.innerHeight - popover.offsetHeight - 8, clientY - popover.offsetHeight))}px`;
    const unsubscribe = this.host.store.select(
      (s) => s.project,
      () => {
        section.refresh();
      },
    );
    const cleanup = (): void => {
      unsubscribe();
      popover.remove();
      this.popoverCleanup = null;
    };
    popover.addEventListener('toggle', (event) => {
      if (event.newState === 'closed') cleanup();
    });
    this.popoverCleanup = cleanup;
  }

  /** Builds the context menu items for the current keyframe selection. */
  private menuItems(): MenuItem[] {
    const { store, playback } = this.host;
    const ids = this.selectedIds();
    const none = ids.length === 0;
    const easing: MenuItem[] = EASING_CHOICES.filter(
      (c) => c.id !== 'cubic' && c.id !== 'steps',
    ).map((choice) => ({
      label: choice.label,
      onSelect: () => {
        setKeyframeEasing(store, ids, defaultEasingFor(choice.id));
      },
    }));
    easing.push({
      label: 'Custom…',
      onSelect: () => {
        const rect = this.canvas.getBoundingClientRect();
        this.openKeyframePopover(ids, rect.left + 80, rect.top + 80);
      },
    });
    return [
      { label: 'Easing', disabled: none, items: easing },
      { separator: true },
      {
        label: 'Copy',
        disabled: none,
        onSelect: () => {
          copySelection(store);
        },
      },
      {
        label: 'Paste at playhead',
        onSelect: () => {
          pasteMemory(store, playback.currentTime);
        },
      },
      {
        label: 'Duplicate at playhead',
        disabled: none,
        onSelect: () => {
          duplicateAtPlayhead(store, playback.currentTime);
        },
      },
      {
        label: 'Delete',
        disabled: none,
        onSelect: () => {
          deleteKeyframes(store, ids);
        },
      },
      { separator: true },
      {
        label: 'Reverse timing',
        disabled: ids.length < 2,
        onSelect: () => {
          reverseTiming(store, ids);
        },
      },
      {
        label: 'Distribute evenly',
        disabled: !canDistribute(store.getState().project, ids),
        onSelect: () => {
          distributeKeyframes(store, ids);
        },
      },
    ];
  }

  /** Right-click: select what is under the pointer if needed, then show the menu. */
  private onContextMenu(event: MouseEvent): void {
    event.preventDefault();
    const { x, y } = this.local(event);
    const mark = hitTestMark(this.host.getMarks(), this.host.getRows(), this.host.getView(), x, y);
    if (mark && !mark.ids.every((id) => this.selectedIds().includes(id))) {
      selectKeyframesWithElements(this.host.store, mark.ids);
    }
    showContextMenu(event.clientX, event.clientY, this.menuItems());
  }

  /** Rebuilds the visually hidden keyframe buttons, keeping keyboard focus on the same keyframe. */
  private rebuildKeyList(): void {
    const focusedId =
      document.activeElement instanceof HTMLElement
        ? document.activeElement.dataset['keyframe']
        : undefined;
    const { project } = this.host.store.getState();
    const buttons: HTMLElement[] = [];
    for (const mark of this.host.getMarks()) {
      if (mark.merged || !mark.property) continue;
      const element = project.elements[mark.elementId]?.label ?? mark.elementId;
      const button = h('button', {
        type: 'button',
        'data-keyframe': mark.id,
        'aria-label': `${element} ${PROPERTIES[mark.property].label} keyframe at ${formatTime(mark.time)}`,
      });
      button.addEventListener('focus', () => {
        if (!this.selectedIds().includes(mark.id) || this.selectedIds().length !== 1) {
          selectKeyframesWithElements(this.host.store, [mark.id]);
        }
      });
      button.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          const step = (event.shiftKey ? 100 : 10) * (event.key === 'ArrowLeft' ? -1 : 1);
          setKeyframeTime(this.host.store, mark.id, mark.time + step);
        } else if (event.key === 'Delete' || event.key === 'Backspace') {
          event.preventDefault();
          deleteKeyframes(this.host.store, [mark.id]);
        } else if ((event.key === 'F10' && event.shiftKey) || event.key === 'ContextMenu') {
          event.preventDefault();
          const canvas = this.canvas.getBoundingClientRect();
          const position = markPosition(mark, this.host.getRows(), this.host.getView());
          showContextMenu(canvas.left + position.x, canvas.top + position.y, this.menuItems());
        }
      });
      buttons.push(button);
    }
    this.keyList.replaceChildren(...buttons);
    if (focusedId)
      this.keyList
        .querySelector<HTMLElement>(`[data-keyframe="${focusedId}"]`)
        ?.focus({ preventScroll: true });
  }

  /** Selects every keyframe on a track, for the track label's context menu. */
  selectTrack(trackId: string): void {
    selectAllKeyframesOn(this.host.store, [trackId]);
  }
}
